import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, Brain, Shield, Cpu, HeartCrack, Check, ExternalLink,
  TrendingUp, Clock, Wallet, Activity, RefreshCw,
} from 'lucide-react';
import { IMAGES, SITE } from '../mock';
import ServicesGrid from '../components/ServicesGrid';
import Newsletter from '../components/Newsletter';
import SEO, { serviceSchema } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONCEPT = [
  { icon: Brain, title: 'KI-Analyse', text: 'Echtzeit-Erkennung von Trends und Chancen – datengetrieben und ohne Verzögerung.' },
  { icon: Shield, title: 'Risk Management', text: 'Gewinne sichern, Verluste begrenzen. Klare Drawdown-Limits & manuelle Überwachung.' },
  { icon: Cpu, title: 'Vollautomatisch', text: 'Analyse, Entscheidung und Ausführung in einem geschlossenen, regelbasierten System.' },
  { icon: HeartCrack, title: 'Emotionsfrei', text: 'Konsistente Ausführung – keine Angst, keine Gier, unabhängig vom Marktklima.' },
];

const STEPS = [
  { n: '01', title: 'Investitionsgröße definieren', text: 'Entscheide, wie viel Kapital du investieren willst. Wichtig: investiere nur Kapital, dessen Verlust im schlimmsten Fall verkraftbar wäre.' },
  { n: '02', title: 'Broker-Konto eröffnen', text: 'Du eröffnest dein eigenes Trading-Konto bei einem unserer Partner-Broker und zahlst den gewünschten Betrag ein. Konto bleibt in deinem Namen.' },
  { n: '03', title: 'Konto verbinden', text: 'Nach Abschluss aller Schritte verbinden wir dein Broker-Konto mit unserem Trading-System.' },
  { n: '04', title: 'Trading startet', text: 'Unser System analysiert Charts in den Futures-Märkten, findet optimale Einstiege und führt Trades nach unserem Risk-Management aus.' },
  { n: '05', title: 'Volle Kontrolle', text: 'Verfolge die Kapitalentwicklung in Echtzeit über dein Broker-Konto und dein persönliches Dashboard.' },
];

const FALLBACK = {
  cumulative_pct: 249.32,
  best_month_pct: 32.95,
  worst_month_pct: -10.89,
  max_drawdown_pct: -11.19,
  performance_fee_pct: 40,
  min_deposit: 2000,
  strategy: 'Alpha AI V2',
  monthly_returns: [],
  scraped_at: null,
};

function formatPct(v, opts = {}) {
  if (v === null || v === undefined) return '–';
  const num = Number(v);
  const sign = num > 0 ? '+' : (num < 0 ? '' : '');
  const decimals = opts.decimals ?? 2;
  return `${sign}${num.toFixed(decimals)} %`;
}

function formatUsd(v) {
  if (!v) return '–';
  return `${Number(v).toLocaleString('de-CH').replace(/,/g, '’')} $`;
}

function LiveBadge({ scrapedAt }) {
  if (!scrapedAt) return null;
  const d = new Date(scrapedAt);
  const fmt = d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0e0e0c] border border-[#9ce7a8]/25 text-[10px] uppercase tracking-[0.22em] text-[#9ce7a8]">
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#9ce7a8] opacity-75" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#9ce7a8]" />
      </span>
      Live · aktualisiert {fmt}
    </div>
  );
}

export default function Alphalane() {
  const [stats, setStats] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/alphalane/stats`)
      .then(({ data }) => { if (!cancelled) setStats({ ...FALLBACK, ...data }); })
      .catch(() => { /* keep fallback */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const terms = [
    { label: 'Min. Einlage', value: stats.min_deposit ? `ab ${formatUsd(stats.min_deposit).replace(' $', '')} USD` : 'ab 2’000 USD' },
    { label: 'Performance Fee', value: `${stats.performance_fee_pct ?? 40} % auf Gewinne` },
    { label: 'Laufzeit', value: 'individuell, keine Bindung' },
    { label: 'Auszahlungen', value: 'innerhalb einer Woche' },
    { label: 'Kontoinhaber', value: 'ausschließlich auf deinen Namen' },
    { label: 'Transparenz', value: 'eigenes Dashboard, Echtzeit' },
  ];

  const heroStats = [
    { value: '24/5', label: 'aktiv im Markt' },
    { value: formatPct(stats.cumulative_pct, { decimals: 2 }), label: 'kumuliert seit Mai ’25' },
    { value: '5–20 %', label: 'angestrebte Monatsrendite' },
    { value: stats.min_deposit ? formatUsd(stats.min_deposit).replace(' $', '') + ' $' : '2’000 $', label: 'Mindesteinlage' },
  ];

  const recent = (stats.monthly_returns || []).slice(-6);

  return (
    <div>
      <SEO
        path="/alphalane"
        title="Alphalane AI Trading – KI-gesteuertes Trading Schweiz"
        description="Vollautomatisches KI-Trading aus der Schweiz. Dein Kapital bleibt auf deinem Konto. Transparenter Track Record, performance-basiert, ab 2’000 USD."
        schema={serviceSchema({ name: 'Alphalane AI Trading', description: 'KI-gesteuertes, vollautomatisches Futures-Trading auf dem eigenen Broker-Konto.', url: '/alphalane' })}
      />
      {/* HERO */}
      <section className="relative pt-36 pb-12 px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/3 -left-32 w-[500px] h-[500px] rounded-full bg-[#d4b574]/10 blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#b8945a]/6 blur-[100px]" />
        </div>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#d4b574]/10 border border-[#d4b574]/25 text-[11px] uppercase tracking-[0.24em] text-[#e8d5a8] mb-6">
              <Activity size={12} /> KI-gesteuertes Trading · 24/5
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl text-[#f5efe3] leading-[1.05]">
              Profitiere von den Märkten – <span className="italic text-gold-grad">ohne selbst zu traden.</span>
            </h1>
            <p className="mt-6 text-[#cfc6b5] text-lg leading-relaxed">
              <span className="text-[#e8d5a8]">Alphalane AI</span> verwaltet dein Trading-Konto vollautomatisch nach klaren, regelbasierten Kriterien. Dein Geld bleibt zu 100 % auf deinen Namen – wir traden, du wächst.
            </p>
            <ul className="mt-7 space-y-2.5">
              {[
                'Vollautomatisches Trading auf deinem eigenen Konto',
                'Manuelle Überwachung durch unser Team',
                'Audited & gesichertes Risk-Management',
                'Diversifiziert über mehrere Futures-Märkte',
                'Jederzeit pausierbar, kündbar mit 1 Woche Frist',
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-[#cfc6b5]">
                  <Check size={18} className="text-[#d4b574] mt-0.5 flex-shrink-0" /> {b}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={SITE.alphalaneUrl} target="_blank" rel="noreferrer" className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#d4b574] text-[#0a0a0c] font-medium hover:bg-[#e8d5a8] transition-colors">
                Zu Alphalane <ExternalLink size={15} />
              </a>
              <Link to="/kontakt" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-[#cfc6b5] hover:border-[#d4b574]/40 hover:text-[#e8d5a8] transition-all">
                Beratung anfragen
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#d4b574]/20 to-transparent rounded-3xl blur-2xl" />
            <div className="relative rounded-3xl overflow-hidden border border-[#d4b574]/15">
              <img src={IMAGES.alphalane} alt="Alphalane AI Trading" className="w-full h-auto" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c]/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 p-4 rounded-2xl bg-[#0a0a0c]/70 backdrop-blur-md border border-[#d4b574]/15">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#7a7060]">Strategie</div>
                    <div className="text-sm text-[#e8d5a8] font-medium">{stats.strategy || 'Alpha AI V2'} · MT5</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#7a7060]">Kumuliert</div>
                    <div className="text-sm text-[#9ce7a8] font-medium">{formatPct(stats.cumulative_pct)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="py-10 px-6 lg:px-10 border-y border-white/5 bg-[#08080a]">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {heroStats.map((s, i) => (
            <div key={i} className="text-center">
              <div className="font-serif-display text-3xl md:text-4xl text-gold-grad">{s.value}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.22em] text-[#9a9285]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CONCEPT */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-3">Das Konzept</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3] max-w-3xl mx-auto leading-tight">
              KI-Technologie trifft auf <span className="italic text-gold-grad">bewährte Strategien</span>
            </h2>
            <p className="mt-5 text-[#9a9285] max-w-2xl mx-auto">
              Alphalane verbindet moderne künstliche Intelligenz mit bewährten Trading-Strategien in einem System, das Märkte analysiert, Entscheidungen trifft und Trades automatisch ausführt – stressfrei, emotionsfrei, rund um die Uhr.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {CONCEPT.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className="card-glow rounded-2xl border border-white/8 bg-[#0e0e0c] p-7">
                  <div className="w-12 h-12 rounded-full border border-[#d4b574]/30 flex items-center justify-center text-[#e8d5a8] mb-5">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-serif-display text-xl text-[#f5efe3]">{c.title}</h3>
                  <p className="text-sm text-[#9a9285] leading-relaxed mt-2">{c.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="py-24 px-6 lg:px-10 bg-[#08080a] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-3">So einfach gehts</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3]">
              In <span className="italic text-gold-grad">5 Schritten</span> zum automatisierten Trading
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} className="card-glow rounded-2xl border border-white/8 bg-[#0e0e0c] p-6 relative">
                <div className="font-serif-display text-4xl text-gold-grad opacity-80">{s.n}</div>
                <h3 className="font-serif-display text-lg text-[#f5efe3] mt-3 leading-snug">{s.title}</h3>
                <p className="text-xs text-[#9a9285] leading-relaxed mt-2">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRACK RECORD (LIVE) */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574]">Track Record</div>
            <LiveBadge scrapedAt={stats.scraped_at} />
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3] leading-tight">
            Ergebnisse & <span className="italic text-gold-grad">Kennzahlen</span>
          </h2>
          <p className="mt-5 text-[#9a9285] leading-relaxed max-w-2xl">
            Live-Daten direkt von alphalane-tradingsystems.com, wöchentlich aktualisiert. Diese Daten dienen ausschließlich Informationszwecken – sie sind keine Zusage für zukünftige Resultate.
          </p>

          <div className="mt-10 grid lg:grid-cols-12 gap-6">
            {/* Key metrics */}
            <div className="lg:col-span-7 grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-white/8 bg-[#0e0e0c] p-5">
                <div className="text-xs uppercase tracking-[0.22em] text-[#7a7060]">Kumuliert</div>
                <div className={`font-serif-display text-4xl mt-1 ${loading ? 'opacity-40' : 'text-gold-grad'}`}>
                  {formatPct(stats.cumulative_pct)}
                </div>
                <div className="text-[11px] text-[#7a7060] mt-1">seit Mai 2025</div>
              </div>
              <div className="rounded-2xl border border-white/8 bg-[#0e0e0c] p-5">
                <div className="text-xs uppercase tracking-[0.22em] text-[#7a7060]">Bester Monat</div>
                <div className="font-serif-display text-3xl text-[#9ce7a8] mt-1">{formatPct(stats.best_month_pct)}</div>
              </div>
              <div className="rounded-2xl border border-white/8 bg-[#0e0e0c] p-5">
                <div className="text-xs uppercase tracking-[0.22em] text-[#7a7060]">Schlechtester Monat</div>
                <div className="font-serif-display text-3xl text-[#e8b4a8] mt-1">{formatPct(stats.worst_month_pct)}</div>
              </div>
              <div className="rounded-2xl border border-white/8 bg-[#0e0e0c] p-5">
                <div className="text-xs uppercase tracking-[0.22em] text-[#7a7060]">Max. Zwischenverlust</div>
                <div className="font-serif-display text-3xl text-[#cfc6b5] mt-1">{formatPct(stats.max_drawdown_pct)}</div>
              </div>
            </div>

            {/* Performance goal box */}
            <div className="lg:col-span-5 rounded-3xl border border-[#d4b574]/15 bg-gradient-to-br from-[#13130f] to-[#0a0a08] p-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#7a7060]">Performance-Ziel</div>
                  <div className="font-serif-display text-3xl text-[#e8d5a8] mt-1">5 – 20 % / Monat</div>
                </div>
                <TrendingUp size={32} className="text-[#d4b574]" />
              </div>
              <p className="mt-5 text-sm text-[#9a9285] leading-relaxed">
                Fokus auf minimalen Zwischenverlust pro Trade. Die daraus resultierende Rendite kann nie garantiert werden – das Alphalane-Team strebt jedoch eine monatliche Rendite zwischen 5 und 20 % an.
              </p>
              <a href={SITE.alphalaneUrl} target="_blank" rel="noreferrer" className="mt-7 inline-flex items-center gap-2 text-sm text-[#d4b574] link-underline">
                Vollständigen Track Record ansehen <ArrowRight size={14} />
              </a>
            </div>
          </div>

          {/* Recent monthly returns */}
          {recent.length > 0 && (
            <div className="mt-10 rounded-3xl border border-white/8 bg-[#0e0e0c] overflow-hidden">
              <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#7a7060]">Aktuelle Monate</div>
                  <div className="font-serif-display text-xl text-[#e8d5a8]">Monatliche Renditen</div>
                </div>
                <RefreshCw size={16} className={`text-[#d4b574] ${loading ? 'animate-spin' : ''}`} />
              </div>
              <div className="divide-y divide-white/5">
                <div className="grid grid-cols-3 px-6 py-3 text-[10px] uppercase tracking-[0.22em] text-[#7a7060]">
                  <div>Monat</div>
                  <div className="text-right">Rendite</div>
                  <div className="text-right">Kumuliert</div>
                </div>
                {recent.map((r) => (
                  <div key={r.month} className="grid grid-cols-3 px-6 py-4 items-center">
                    <div className="text-sm text-[#f5efe3]">{r.month}</div>
                    <div className={`text-right font-medium ${r.monthly_pct >= 0 ? 'text-[#9ce7a8]' : 'text-[#e8b4a8]'}`}>{formatPct(r.monthly_pct)}</div>
                    <div className="text-right text-[#cfc6b5]">{formatPct(r.cumulative_pct)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* TERMS */}
      <section className="py-24 px-6 lg:px-10 bg-[#08080a] border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-3">Konditionen</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3]">
              Klare Konditionen. <span className="italic text-gold-grad">Keine Überraschungen.</span>
            </h2>
            <p className="mt-4 text-[#9a9285] max-w-2xl mx-auto">
              Performance-basiertes Modell: Alphalane verdient nur, wenn du verdienst. Keine Fixkosten, keine Gebühren in Verlustmonaten.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {terms.map((t) => (
              <div key={t.label} className="flex items-center justify-between px-6 py-5 rounded-xl border border-white/8 bg-[#0e0e0c]">
                <div className="text-sm text-[#9a9285] uppercase tracking-[0.18em]">{t.label}</div>
                <div className="text-[#e8d5a8] font-medium">{t.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TARGET */}
      <section className="py-20 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-3">Für wen ist das?</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3]">Für alle, die <span className="italic text-gold-grad">smart investieren</span> wollen</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: Wallet, title: 'Investoren', text: 'Die von den Märkten profitieren möchten, ohne selbst zu traden.' },
              { icon: Activity, title: 'Trader', text: 'Auf der Suche nach einem zuverlässigen, emotionsfreien System.' },
              { icon: Clock, title: 'Profis', text: 'Die auf Geschwindigkeit, Datenqualität und Struktur Wert legen.' },
            ].map((g) => {
              const Icon = g.icon;
              return (
                <div key={g.title} className="card-glow rounded-2xl border border-white/8 bg-[#0e0e0c] p-7">
                  <Icon size={22} className="text-[#d4b574]" />
                  <h3 className="font-serif-display text-2xl text-[#f5efe3] mt-4">{g.title}</h3>
                  <p className="text-sm text-[#9a9285] mt-2 leading-relaxed">{g.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto rounded-3xl border border-[#d4b574]/20 bg-gradient-to-br from-[#13130f] to-[#0a0a08] p-10 md:p-16 text-center">
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3]">
            Bereit für den <span className="italic text-gold-grad">nächsten Schritt</span>?
          </h2>
          <p className="mt-5 text-[#9a9285] max-w-xl mx-auto">
            Lass uns deine Situation kennenlernen – unverbindlich, individuell, schnell. Üblicherweise melden wir uns innerhalb von 24 Stunden.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href={SITE.alphalaneUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#d4b574] text-[#0a0a0c] font-medium hover:bg-[#e8d5a8] transition-colors">
              Zu Alphalane <ExternalLink size={15} />
            </a>
            <Link to="/kontakt" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-[#cfc6b5] hover:border-[#d4b574]/40 hover:text-[#e8d5a8] transition-all">
              Kontaktformular
            </Link>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="px-6 lg:px-10 py-16 border-t border-white/5 bg-[#08080a]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-3">Weitere Angebote</div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#f5efe3]">Entdecke mehr <span className="italic text-gold-grad">Lösungen</span></h2>
            </div>
            <Link to="/angebot" className="hidden md:inline-flex items-center gap-2 text-sm text-[#d4b574] hover:gap-3 transition-all">
              Alle Angebote <ArrowRight size={14} />
            </Link>
          </div>
          <ServicesGrid excludeId="alphalane" />
        </div>
      </section>

      <Newsletter />
    </div>
  );
}
